// services/medicationservice2.js - Uses meds2 table, no phases/tab functionality
// UPDATED: Added user-specific data isolation
import { 
  collection, 
  addDoc, 
  getDoc,
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query,
  where  // ADDED: for filtering by userId
} from 'firebase/firestore';
import { db, auth } from '../firebase'; // UPDATED: import auth

// Collection name - DIFFERENT from original (meds2 instead of meds)
const MEDICATIONS_COLLECTION = 'meds2';

// ────────────────────────────────────────────────
// Helper to get current user ID
// ────────────────────────────────────────────────
const getCurrentUserId = () => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  return user.uid;
};

// ────────────────────────────────────────────────
// UNIVERSAL Date converter - handles BOTH formats
// ────────────────────────────────────────────────
const convertToDateString = (dateValue) => {
  if (!dateValue) return null;
  
  // 1. If it's already a string, return it
  if (typeof dateValue === 'string') {
    return dateValue;
  }
  
  // 2. If it's a Firestore Timestamp object
  if (dateValue.toDate && typeof dateValue.toDate === 'function') {
    const date = dateValue.toDate();
    return dateToLocalISOString(date);
  }
  
  // 3. If it's already a Date object
  if (dateValue instanceof Date) {
    return dateToLocalISOString(dateValue);
  }
  
  // 4. If it's a number (timestamp)
  if (typeof dateValue === 'number') {
    return dateToLocalISOString(new Date(dateValue));
  }
  
  console.warn('Unknown date format:', dateValue);
  return null;
};

// Convert Date object to ISO string without timezone issues
const dateToLocalISOString = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  
  // Get local date components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
  
  // Create ISO-like string in local time
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

// Convert string to Date object
const stringToDate = (dateString) => {
  if (!dateString) return null;
  
  if (typeof dateString === 'string') {
    // Try parsing as ISO string
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
    
    // Try parsing as Firestore Timestamp string format
    if (dateString.includes('Timestamp')) {
      try {
        // Handle Firestore timestamp strings like "Timestamp(seconds=..., nanoseconds=...)"
        const match = dateString.match(/Timestamp\(seconds=(\d+), nanoseconds=(\d+)\)/);
        if (match) {
          const seconds = parseInt(match[1], 10);
          const nanoseconds = parseInt(match[2], 10);
          const timestamp = seconds * 1000 + nanoseconds / 1000000;
          return new Date(timestamp);
        }
      } catch (error) {
        console.warn('Failed to parse timestamp string:', error);
      }
    }
  }
  
  return null;
};

// ────────────────────────────────────────────────
// Time component reconstruction
// ────────────────────────────────────────────────

// Convert stored components back to Date object
export const reconstructDate = (hour, minute, period, dateStr) => {
  let hours24 = hour;
  
  // Convert 12-hour to 24-hour
  if (period === 'AM') {
    if (hour === 12) {
      hours24 = 0;  // 12 AM = 0 (midnight)
    } else {
      hours24 = hour;  // 1-11 AM = 1-11
    }
  } else { // PM
    if (hour === 12) {
      hours24 = 12;  // 12 PM = 12 (noon)
    } else {
      hours24 = hour + 12;  // 1-11 PM = 13-23
    }
  }
  // Parse YYYY-MM-DD
  const [year, month, day] = dateStr.split('-').map(Number);
  
  // Create date in LOCAL timezone
  return new Date(year, month - 1, day, hours24, minute, 0, 0);
};

// Parse custom time string into components - FIXED TO HANDLE 24-HOUR FORMAT
export const parseTimeComponents = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') {
    throw new Error('Invalid time string');
  }
  
  let cleaned = timeStr.trim();
  let period = null;
  let hour24 = null;
  
  // Extract AM/PM if present
  if (cleaned.includes(' AM') || cleaned.includes(' PM')) {
    const parts = cleaned.split(/\s+/);
    cleaned = parts[0];
    period = parts[1]?.toUpperCase();
  }
  
  // Parse hours:minutes
  const [hourStr, minuteStr] = cleaned.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr || '0', 10);
  
  if (isNaN(hour) || isNaN(minute)) {
    throw new Error('Invalid time format');
  }
  
  // FIX: If hour is in 24-hour format (13-23), convert to 12-hour and set period
  if (hour >= 13 && hour <= 23) {
    period = 'PM';
    hour = hour - 12;
  } else if (hour === 0) {
    // Midnight in 24-hour format
    hour = 12;
    period = 'AM';
  } else if (hour === 12 && !period) {
    // Noon in 24-hour format - assume PM if no period specified
    period = 'PM';
  } else if (!period) {
    // For hours 1-11 without period, we need to infer or default
    // This shouldn't happen in normal use, but default to AM for safety
    period = 'AM';
  }
  
  return { hour, minute, period };
};

// ────────────────────────────────────────────────
// Parse prescription (1x1 → 24h, 1x2 → 12h, etc.)
// ────────────────────────────────────────────────
const parsePrescription = (prescription) => {
  if (!prescription || typeof prescription !== 'string') {
    throw new Error('Invalid prescription');
  }

  const cleaned = prescription.trim().toLowerCase().replace(/\s+/g, '');
  const match = cleaned.match(/^(\d+(?:\.\d+)?)x(\d+)$/);

  if (!match) {
    throw new Error('Invalid format. Use e.g. 1x1, 1x2, 2x3');
  }

  const dosePerIntake = parseFloat(match[1]);
  const timesPerDay   = parseInt(match[2], 10);

  if (isNaN(dosePerIntake) || isNaN(timesPerDay) || dosePerIntake <= 0 || timesPerDay <= 0) {
    throw new Error('Invalid numbers in prescription');
  }

  if (timesPerDay > 24) {
    throw new Error('Cannot take more than 24 times per day');
  }

  const intervalHours = 24 / timesPerDay;

  return {
    dosePerIntake,
    timesPerDay,
    intervalHours: Number(intervalHours.toFixed(2))
  };
};

export const validatePrescription = (prescription) => {
  try {
    const parsed = parsePrescription(prescription);
    return {
      valid: true,
      ...parsed,
      description: `${parsed.dosePerIntake} pill${parsed.dosePerIntake !== 1 ? 's' : ''}, ${parsed.timesPerDay} time${parsed.timesPerDay !== 1 ? 's' : ''} daily`
    };
  } catch (err) {
    return {
      valid: false,
      message: err.message || 'Invalid prescription format'
    };
  }
};

export const getPrescriptionDescription = (prescription) => {
  const validation = validatePrescription(prescription);
  return validation.valid ? validation.description : prescription;
};

// ────────────────────────────────────────────────
// Core timing functions - handle BOTH formats
// ────────────────────────────────────────────────
export const calculateNextDose = (lastTakenValue, intervalHours) => {
  if (!lastTakenValue || !intervalHours) return null;
  
  // Convert whatever format we have to a Date object
  let lastDate;
  
  if (lastTakenValue instanceof Date) {
    lastDate = lastTakenValue;
  } else if (typeof lastTakenValue === 'string') {
    lastDate = stringToDate(lastTakenValue);
  } else if (lastTakenValue.toDate && typeof lastTakenValue.toDate === 'function') {
    // Firestore Timestamp
    lastDate = lastTakenValue.toDate();
  }
  
  if (!lastDate || isNaN(lastDate.getTime())) return null;
  
  const next = new Date(lastDate);
  next.setTime(lastDate.getTime() + intervalHours * 60 * 60 * 1000);
  return dateToLocalISOString(next);
};

export const calculateTimeRemaining = (nextDoseValue, intervalHours, lastTakenValue = null) => {
  let effectiveNext;
  
  // Parse next dose time from whatever format
  if (nextDoseValue) {
    if (nextDoseValue instanceof Date) {
      effectiveNext = nextDoseValue;
    } else if (typeof nextDoseValue === 'string') {
      effectiveNext = stringToDate(nextDoseValue);
    } else if (nextDoseValue.toDate && typeof nextDoseValue.toDate === 'function') {
      effectiveNext = nextDoseValue.toDate();
    }
  }
  
  // Recalculate from last taken if available
  if (lastTakenValue && intervalHours) {
    let lastDate;
    
    if (lastTakenValue instanceof Date) {
      lastDate = lastTakenValue;
    } else if (typeof lastTakenValue === 'string') {
      lastDate = stringToDate(lastTakenValue);
    } else if (lastTakenValue.toDate && typeof lastTakenValue.toDate === 'function') {
      lastDate = lastTakenValue.toDate();
    }
    
    if (lastDate && !isNaN(lastDate.getTime())) {
      effectiveNext = new Date(lastDate.getTime() + intervalHours * 3600000);
    }
  }

  if (!effectiveNext || isNaN(effectiveNext.getTime())) {
    return { hours: 0, minutes: 0, totalMinutes: 0, isOverdue: false, overdueMinutes: 0, absTotalMinutes: 0 };
  }

  const now = new Date();
  const diffMs = effectiveNext.getTime() - now.getTime();
  let totalMinutes = Math.floor(diffMs / 60000);
  const isOverdue = totalMinutes < 0;
  const overdueMinutes = isOverdue ? -totalMinutes : 0;

  const maxMinutes = intervalHours * 60;
  if (!isOverdue && totalMinutes > maxMinutes) totalMinutes = maxMinutes;

  const absTotalMinutes = Math.abs(totalMinutes);
  return {
    hours: Math.floor(absTotalMinutes / 60),
    minutes: absTotalMinutes % 60,
    totalMinutes,
    isOverdue,
    overdueMinutes,
    absTotalMinutes
  };
};

export const calculateTimeSinceLastTaken = (lastTakenValue, history = []) => {
  if (!lastTakenValue) return null;
  
  let lastDate = null;
  
  // Try to get the most accurate date from history first
  if (history && history.length > 0) {
    const latestEntry = history[0];
    
    // Use the stored components if available
    if (latestEntry.hour !== undefined && latestEntry.dateStr) {
      lastDate = reconstructDate(
        latestEntry.hour, 
        latestEntry.minute, 
        latestEntry.period, 
        latestEntry.dateStr
      );
      console.log('Reconstructed from history:', {
        from: `${latestEntry.hour}:${latestEntry.minute} ${latestEntry.period} on ${latestEntry.dateStr}`,
        to: lastDate.toString()
      });
    }
    // Fall back to dateString
    else if (latestEntry.dateString) {
      lastDate = stringToDate(latestEntry.dateString);
    }
  }
  
  // If we couldn't get date from history, use lastTakenValue
  if (!lastDate && lastTakenValue) {
    if (lastTakenValue instanceof Date) {
      lastDate = lastTakenValue;
    } else if (typeof lastTakenValue === 'string') {
      lastDate = stringToDate(lastTakenValue);
    }
  }
  
  if (!lastDate || isNaN(lastDate.getTime())) {
    console.warn('Could not parse lastTaken date:', lastTakenValue);
    return null;
  }

  const now = new Date();
  const diffMs = now.getTime() - lastDate.getTime();
  
  // If the date is in the future, return 0
  if (diffMs < 0) return { days: 0, hours: 0, minutes: 0, totalMinutes: 0 };

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const days = Math.floor(hours / 24);
  
  return {
    days,
    hours: hours % 24,
    minutes,
    totalMinutes
  };
};

export const formatTimeSince = (timeSince) => {
  if (!timeSince) return 'Never';
  const { days, hours, minutes } = timeSince;
  if (days > 0)   return `${days}d ${hours}h ${minutes}m ago`;
  if (hours > 0)  return `${hours}h ${minutes}m ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
};

export const formatTime = (dateValue) => {
  if (!dateValue) return '';
  
  let date;
  
  // Handle all formats
  if (dateValue instanceof Date) {
    date = dateValue;
  } else if (typeof dateValue === 'string') {
    date = stringToDate(dateValue);
  } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  }
  
  if (!date || isNaN(date.getTime())) return '';
  
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format time with date context
export const formatTimeWithDate = (dateValue) => {
  if (!dateValue) return '';
  
  let date;
  
  // Handle all formats
  if (dateValue instanceof Date) {
    date = dateValue;
  } else if (typeof dateValue === 'string') {
    date = stringToDate(dateValue);
  } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  }
  
  if (!date || isNaN(date.getTime())) return '';
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  
  const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  // Check if same day
  if (entryDate.getTime() === today.getTime()) {
    return `Today at ${timeStr}`;
  }
  // Check if tomorrow
  else if (entryDate.getTime() === tomorrow.getTime()) {
    return `Tomorrow at ${timeStr}`;
  }
  // Check if yesterday
  else if (entryDate.getTime() === yesterday.getTime()) {
    return `Yesterday at ${timeStr}`;
  }
  // Other day
  else {
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    return `${dateStr} at ${timeStr}`;
  }
};

export const getFormattedHistory = (history = []) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  return history
    .map(entry => {
      let date;
      
      // 1. Try to use stored components first (most reliable)
      if (entry.hour !== undefined && entry.dateStr) {
        date = reconstructDate(entry.hour, entry.minute, entry.period, entry.dateStr);
      } 
      // 2. Try dateString field (new format)
      else if (entry.dateString && typeof entry.dateString === 'string') {
        date = stringToDate(entry.dateString);
      }
      // 3. Try date field (could be string OR Firestore Timestamp)
      else if (entry.date) {
        if (entry.date.toDate && typeof entry.date.toDate === 'function') {
          // Firestore Timestamp
          date = entry.date.toDate();
        } else if (typeof entry.date === 'string') {
          // Already a string
          date = stringToDate(entry.date);
        } else if (entry.date instanceof Date) {
          date = entry.date;
        }
      }
      
      let displayTime = entry.customTime || '';
      let relativeDate = '';
      
      if (date && !isNaN(date.getTime())) {
        // Format time part
        const timeStr = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        
        // Determine if today, yesterday, or older
        const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        if (entryDate.getTime() === today.getTime()) {
          relativeDate = 'Today';
          displayTime = `${timeStr}`;
        } else if (entryDate.getTime() === yesterday.getTime()) {
          relativeDate = 'Yesterday';
          displayTime = `${timeStr}`;
        } else {
          // Older dates - show date
          const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          });
          relativeDate = dateStr;
          displayTime = `${timeStr}`;
        }
      }
      
      return {
        ...entry,
        date,
        formattedTime: date && !isNaN(date.getTime()) ? formatTime(date) : '',
        customTime: entry.customTime || '',
        displayTime,      // Time only (e.g., "10:18 AM")
        relativeDate,     // "Today", "Yesterday", or "Dec 16"
        fullDisplay: relativeDate ? `${relativeDate} at ${displayTime}` : displayTime,
        isCustomTime: entry.type === 'taken_custom'
      };
    })
    .sort((a, b) => {
      const timeA = a.date ? a.date.getTime() : 0;
      const timeB = b.date ? b.date.getTime() : 0;
      return timeB - timeA;
    });
};

export const getFrequencyDescription = (timesPerDay) => {
  if (!timesPerDay) return 'Not set';
  if (timesPerDay === 1) return 'Once daily';
  if (timesPerDay === 2) return 'Twice daily';
  return `${timesPerDay} times daily`;
};

// Safe converter that returns Date object
export const safeToDate = (dateValue) => {
  if (!dateValue) return null;
  
  if (dateValue instanceof Date) {
    return dateValue;
  } else if (typeof dateValue === 'string') {
    return stringToDate(dateValue);
  } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
    return dateValue.toDate();
  }
  
  return null;
};

// ────────────────────────────────────────────────
// Current time in 12-hour format
// ────────────────────────────────────────────────
export const getCurrentTime12Hour = () => {
  return new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Get current date as YYYY-MM-DD string
export const getCurrentDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get current datetime as string
export const getCurrentDateTimeString = () => {
  return dateToLocalISOString(new Date());
};

// ────────────────────────────────────────────────
// Firestore CRUD - User-specific data isolation
// ────────────────────────────────────────────────

// UPDATED: Filter medications by current user
export const getMedications = async () => {
  const userId = getCurrentUserId();
  
  // Query only medications belonging to this user
  const q = query(
    collection(db, MEDICATIONS_COLLECTION), 
    where('userId', '==', userId)
  );
  
  const snapshot = await getDocs(q);
  const meds = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    const validation = validatePrescription(data.prescription || '');
    if (!validation.valid) continue;

    const { dosePerIntake, timesPerDay, intervalHours } = validation;
    
    // Convert lastTaken from whatever format it's in
    const lastTaken = convertToDateString(data.lastTaken);
    
    const nextDose = calculateNextDose(lastTaken, intervalHours);

    meds.push({
      id: docSnap.id,
      name: data.drug || 'Unnamed',
      prescription: data.prescription,
      dosePerIntake,
      timesPerDay,
      intervalHours,
      lastTaken: lastTaken ? new Date(lastTaken) : null,
      lastTakenString: lastTaken,
      nextDose: nextDose ? new Date(nextDose) : null,
      nextDoseString: nextDose,
      timeRemaining: calculateTimeRemaining(nextDose, intervalHours, lastTaken),
      timeSince: calculateTimeSinceLastTaken(lastTaken, data.history || []),
      isOverdue: calculateTimeRemaining(nextDose, intervalHours, lastTaken).isOverdue,
      history: getFormattedHistory(data.history || []),
      createdAt: convertToDateString(data.createdAt),
      reminders: data.reminders ?? { before30Min: true, before10Min: true, atExactTime: true },
      userId: data.userId
    });
  }

  // Sort by createdAt descending (newest first)
  return meds.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });
};

// UPDATED: Add medication with current user's ID
// In medicationservice2.js - Update the addMedication function
export const addMedication = async (name, prescription, reminders = {}) => {
  const userId = getCurrentUserId();
  const validation = validatePrescription(prescription);
  if (!validation.valid) throw new Error(validation.message);

  const now = new Date();
  const data = {
    drug: name.trim(),
    prescription: prescription.trim(),
    dosePerIntake: validation.dosePerIntake,
    timesPerDay: validation.timesPerDay,
    intervalHours: validation.intervalHours,
    userId: userId,
    createdAt: dateToLocalISOString(now), // Use the function directly
    reminderSent: false,
    lastReminderSentAt: null,
    lastTaken: null,
    history: [],
    reminders: {
      before30Min: reminders.before30Min ?? true,
      before10Min: reminders.before10Min ?? true,
      atExactTime: true
    }
  };

  const ref = await addDoc(collection(db, MEDICATIONS_COLLECTION), data);
  
  // Return fully formatted medication object
  return {
    id: ref.id,
    name: data.drug,
    prescription: data.prescription,
    dosePerIntake: data.dosePerIntake,
    timesPerDay: data.timesPerDay,
    intervalHours: data.intervalHours,
    lastTaken: null,
    lastTakenString: null,
    nextDose: null,
    nextDoseString: null,
    timeRemaining: { hours: 0, minutes: 0, totalMinutes: 0, isOverdue: false, overdueMinutes: 0, absTotalMinutes: 0 },
    timeSince: null,
    isOverdue: false,
    history: [],
    createdAt: now,
    reminders: data.reminders,
    userId: data.userId
  };
};

// UPDATED: Verify ownership before updating
export const updateMedication = async (id, name, prescription, reminders = {}) => {
  const userId = getCurrentUserId();
  const validation = validatePrescription(prescription);
  if (!validation.valid) throw new Error(validation.message);

  // First verify this medication belongs to the user
  const ref = doc(db, MEDICATIONS_COLLECTION, id);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) throw new Error('Medication not found');
  if (snap.data().userId !== userId) throw new Error('Unauthorized');

  // Use passed reminders, falling back to existing ones in Firestore
  const existingReminders = snap.data().reminders ?? {};

  const updateData = {
    drug: name.trim(),
    prescription: prescription.trim(),
    dosePerIntake: validation.dosePerIntake,
    timesPerDay: validation.timesPerDay,
    intervalHours: validation.intervalHours,
    updatedAt: getCurrentDateTimeString(),
    reminders: {
      before30Min: reminders.before30Min ?? existingReminders.before30Min ?? true,
      before10Min: reminders.before10Min ?? existingReminders.before10Min ?? true,
      atExactTime: true  // always true
    }
  };

  await updateDoc(ref, updateData);

  const freshSnap = await getDoc(ref);
  const data = freshSnap.data();
  
  const lastTaken = convertToDateString(data.lastTaken);
  const nextDose = calculateNextDose(lastTaken, data.intervalHours);

  return {
    id,
    name: data.drug,
    prescription: data.prescription,
    dosePerIntake: data.dosePerIntake,
    timesPerDay: data.timesPerDay,
    intervalHours: data.intervalHours,
    lastTaken: lastTaken ? new Date(lastTaken) : null,
    lastTakenString: lastTaken,
    nextDose: nextDose ? new Date(nextDose) : null,
    nextDoseString: nextDose,
    timeRemaining: calculateTimeRemaining(nextDose, data.intervalHours, lastTaken),
    timeSince: calculateTimeSinceLastTaken(lastTaken, data.history || []),
    history: getFormattedHistory(data.history || []),
    createdAt: convertToDateString(data.createdAt),
    reminders: data.reminders,
    userId: data.userId
  };
};

// UPDATED: Verify ownership before taking medication
export const takeMedication = async (id, isTakingNow, customTimeStr = null) => {
  const userId = getCurrentUserId();

  const ref = doc(db, MEDICATIONS_COLLECTION, id);
  const snap = await getDoc(ref);

  if (!snap.exists()) throw new Error('Medication not found');
  if (snap.data().userId !== userId) throw new Error('Unauthorized');

  let takenTime;
  let hour, minute, period, dateStr;

  // ─────────────────────────────────────────────
  // TAKE NOW
  // ─────────────────────────────────────────────
  if (isTakingNow) {
    takenTime = new Date();

    const hour24 = takenTime.getHours();
    minute = takenTime.getMinutes();

    // AM/PM
    period = hour24 >= 12 ? 'PM' : 'AM';

    // convert to 12-hour
    hour = hour24 % 12;
    if (hour === 0) hour = 12;

    dateStr = getCurrentDateString();
  }

  // ─────────────────────────────────────────────
  // CUSTOM TIME
  // ─────────────────────────────────────────────
  else {
    if (!customTimeStr) throw new Error('Custom time required');

    const components = parseTimeComponents(customTimeStr);
    hour = components.hour;
    minute = components.minute;
    period = components.period;

    dateStr = getCurrentDateString();
    takenTime = reconstructDate(hour, minute, period, dateStr);
  }

  // ─────────────────────────────────────────────
  // HISTORY ENTRY
  // ─────────────────────────────────────────────
  const entry = {
    hour,
    minute,
    period,
    dateStr,
    dateString: dateToLocalISOString(takenTime),
    customTime: customTimeStr || formatTime(takenTime),
    type: isTakingNow ? 'taken_now' : 'taken_custom',
    loggedAt: getCurrentDateTimeString(),
    isCustomTime: !isTakingNow
  };

  const data = snap.data();
  const history = data.history || [];
  const updatedHistory = [entry, ...history.slice(0, 14)];

  // Calculate nextDose BEFORE update (needed for scheduling reminders)
  const validation = validatePrescription(data.prescription);
  const nextDose = calculateNextDose(
    dateToLocalISOString(takenTime),
    validation.intervalHours
  );

  // ─────────────────────────────────────────────
  // BUILD SCHEDULED NOTIFICATIONS
  // Based on the medication's reminder preferences
  // ─────────────────────────────────────────────
  const med_reminders = data.reminders ?? { before30Min: true, before10Min: true, atExactTime: true };
  const scheduledNotifications = [];

  // Exact time is ALWAYS scheduled
  scheduledNotifications.push({
    sendAt: nextDose,
    type: 'exact',
    sent: false
  });

  if (med_reminders.before30Min) {
    scheduledNotifications.push({
      sendAt: new Date(new Date(nextDose).getTime() - 30 * 60 * 1000).toISOString(),
      type: '30min',
      sent: false
    });
  }

  if (med_reminders.before10Min) {
    scheduledNotifications.push({
      sendAt: new Date(new Date(nextDose).getTime() - 10 * 60 * 1000).toISOString(),
      type: '10min',
      sent: false
    });
  }

  // Get FCM token from user doc so Cloud Function can send the notification
  let fcmToken = null;
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    fcmToken = userSnap.data()?.fcmToken ?? null;
  } catch (e) {
    console.warn('Could not fetch FCM token from user doc:', e);
  }

  await updateDoc(ref, {
    lastTaken: dateToLocalISOString(takenTime),
    nextDose: nextDose,
    history: updatedHistory,
    updatedAt: getCurrentDateTimeString(),
    reminderSent: false,
    lastReminderSentAt: null,
    scheduledNotifications,   // ← Cloud Function reads this
    fcmToken                  // ← Cloud Function uses this to send push
  });

  // ─────────────────────────────────────────────
  // RETURN FRESH VIEW
  // ─────────────────────────────────────────────
  const freshSnap = await getDoc(ref);
  const freshData = freshSnap.data();
  const freshValidation = validatePrescription(freshData.prescription);

  const lastTaken = convertToDateString(freshData.lastTaken);
  const freshNextDose = calculateNextDose(lastTaken, freshValidation.intervalHours);

  return {
    success: true,
    takenTime,
    medication: {
      id,
      name: freshData.drug,
      prescription: freshData.prescription,
      dosePerIntake: freshValidation.dosePerIntake,
      timesPerDay: freshValidation.timesPerDay,
      intervalHours: freshValidation.intervalHours,
      lastTaken: lastTaken ? new Date(lastTaken) : null,
      lastTakenString: lastTaken,
      nextDose: freshNextDose ? new Date(freshNextDose) : null,
      nextDoseString: freshNextDose,
      timeRemaining: calculateTimeRemaining(
        freshNextDose,
        freshValidation.intervalHours,
        lastTaken
      ),
      timeSince: calculateTimeSinceLastTaken(lastTaken),
      history: getFormattedHistory(freshData.history || []),
      createdAt: convertToDateString(freshData.createdAt),
      reminders: freshData.reminders,
      userId: freshData.userId
    }
  };
};

// TEST: Send a browser notification (for testing only)
export const testNotification = async (medicationName = 'Test Medication') => {
  try {
    // Get the stored FCM token from the current user's doc
    const { auth, db } = await import('../firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    const { getFunctions, httpsCallable } = await import('firebase/functions');

    const uid = auth.currentUser?.uid;
    if (!uid) {
      console.warn('[testNotification] No logged-in user');
      return { success: false, error: 'Not logged in' };
    }

    // Read token from users/{uid}
    const userSnap = await getDoc(doc(db, 'users', uid));
    const token = userSnap.data()?.fcmToken;

    if (!token) {
      console.warn('[testNotification] No FCM token found — user needs to enable notifications first');
      return { success: false, error: 'No FCM token. Enable notifications first.' };
    }

    // Call the testNotification Cloud Function
    const functions = getFunctions();
    const callTestNotification = httpsCallable(functions, 'testNotification');
    const result = await callTestNotification({ token, medicationName });

    console.log('[testNotification] ✅ Sent:', result.data);
    return { success: true, ...result.data };

  } catch (err) {
    console.error('[testNotification] Failed:', err);
    return { success: false, error: err.message };
  }
};
// UPDATED: Verify ownership before deleting
export const deleteMedication = async (id) => {
  const userId = getCurrentUserId();
  
  const ref = doc(db, MEDICATIONS_COLLECTION, id);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) throw new Error('Medication not found');
  if (snap.data().userId !== userId) throw new Error('Unauthorized');
  
  await deleteDoc(ref);
};

// ────────────────────────────────────────────────
// Migration helper - run this once to convert all data
// ────────────────────────────────────────────────
export const migrateAllToDateStrings = async () => {
  try {
    console.log('Starting migration to date strings...');
    const snapshot = await getDocs(collection(db, MEDICATIONS_COLLECTION));
    const updates = [];
    
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const updatesForDoc = {};
      
      console.log(`Migrating document ${docSnap.id}...`);
      
      // Convert createdAt if it's a Firestore Timestamp
      if (data.createdAt) {
        updatesForDoc.createdAt = convertToDateString(data.createdAt);
      }
      
      // Convert lastTaken
      if (data.lastTaken) {
        updatesForDoc.lastTaken = convertToDateString(data.lastTaken);
      }
      
      // Convert updatedAt if it exists
      if (data.updatedAt) {
        updatesForDoc.updatedAt = convertToDateString(data.updatedAt);
      }
      
      // Migrate history entries
      if (data.history && data.history.length > 0) {
        const migratedHistory = data.history.map(entry => {
          const migratedEntry = { ...entry };
          
          // If entry has a Firestore Timestamp in date field
          if (entry.date && entry.date.toDate) {
            const date = entry.date.toDate();
            
            // Add components if not already present
            if (entry.hour === undefined) {
              let hour = date.getHours();
              const minute = date.getMinutes();
              let period = hour >= 12 ? 'PM' : 'AM';
              
              // Convert to 12-hour
              if (hour > 12) hour -= 12;
              if (hour === 0) hour = 12;
              
              migratedEntry.hour = hour;
              migratedEntry.minute = minute;
              migratedEntry.period = period;
              
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              migratedEntry.dateStr = `${year}-${month}-${day}`;
            }
            
            // Add dateString field
            migratedEntry.dateString = dateToLocalISOString(date);
            
            // Remove the old timestamp field to save space
            delete migratedEntry.date;
          }
          
          // Ensure loggedAt is a string
          if (entry.loggedAt && entry.loggedAt.toDate) {
            migratedEntry.loggedAt = convertToDateString(entry.loggedAt);
          }
          
          return migratedEntry;
        });
        
        updatesForDoc.history = migratedHistory;
      }
      
      if (Object.keys(updatesForDoc).length > 0) {
        console.log(`Updating ${docSnap.id} with:`, updatesForDoc);
        updates.push(updateDoc(docSnap.ref, updatesForDoc));
      }
    }
    
    await Promise.all(updates);
    console.log(`✅ Migration complete! Updated ${updates.length} medications`);
    return { success: true, count: updates.length };
  } catch (error) {
    console.error('❌ Migration failed:', error);
    return { success: false, error: error.message };
  }
};

// ────────────────────────────────────────────────
// Check if migration is needed
// ────────────────────────────────────────────────
export const checkIfMigrationNeeded = async () => {
  try {
    const snapshot = await getDocs(collection(db, MEDICATIONS_COLLECTION));
    let hasTimestamps = false;
    let totalDocs = 0;
    
    for (const docSnap of snapshot.docs) {
      totalDocs++;
      const data = docSnap.data();
      
      // Check createdAt
      if (data.createdAt && data.createdAt.toDate) {
        hasTimestamps = true;
        break;
      }
      
      // Check history entries
      if (data.history && data.history.some(entry => entry.date && entry.date.toDate)) {
        hasTimestamps = true;
        break;
      }
    }
    
    return {
      needsMigration: hasTimestamps,
      totalDocuments: totalDocs,
      message: hasTimestamps 
        ? 'Found Firestore Timestamps that need migration' 
        : 'All dates are already stored as strings'
    };
  } catch (error) {
    console.error('Error checking migration status:', error);
    return { needsMigration: false, totalDocuments: 0, error: error.message };
  }
};